<template>
  <div v-if="!isExpanded" class="line-item-edit-content collapsed">
    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.description.$errors"
        :input-id="`${id}-description`"
        :label="t('common.description')"
        class="w-3"
      >
        <ProductQuickSearch
          :id="inputId"
          v-model="lineItem"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          @clear="emit('clear')"
        />
      </InputWrapper>

      <UnitOfMeasureInputGroup
        :id="`${id}-handling`"
        v-model="v$.quantity.$model"
        :disabled="disabled"
        :units="[]"
        :errors="v$.quantity.$errors"
        :uom-errors="v$.handlingUnit.$errors"
        :label="t('lineItems.handling')"
        class="w-3"
      >
        <template #uom>
          <RygenDropdown
            :id="`${id}-handling-unit`"
            v-model="v$.handlingUnit.$model"
            :disabled="disabled"
            :options="handlingUnits.data"
            data-key="id"
            option-label="name"
            :loading="handlingUnits.loading"
            filter
            :placeholder="t('uoms.unit')"
          />
        </template>
      </UnitOfMeasureInputGroup>

      <UnitOfMeasureInputGroup
        :id="`${id}-weight`"
        v-model="v$.weight.$model"
        v-model:uom="v$.weightUom.$model"
        :disabled="disabled"
        :units="WEIGHT_UNITS"
        :errors="v$.weight.$errors"
        :uom-errors="v$.weightUom.$errors"
        :label="t('lineItems.weight')"
        class="w-3"
      />

      <DimensionsInputGroup
        :id="`${id}-dims`"
        v-model:length="v$.lengthDim.$model"
        v-model:width="v$.widthDim.$model"
        v-model:height="v$.heightDim.$model"
        v-model:uom="v$.dimensionalUom.$model"
        :disabled="disabled"
        :length-errors="v$.lengthDim.$errors"
        :width-errors="v$.widthDim.$errors"
        :height-errors="v$.heightDim.$errors"
        :uom-errors="v$.dimensionalUom.$errors"
        :units="availableDimsUoms"
        class="w-6"
        optional
      />
    </div>

    <LineItemStopsRow
      v-if="showStops && ShipmentLineItem.isShipmentLineItem(lineItem)"
      v-model="lineItem"
      class="row"
      :disabled="disabled"
      :index="index"
    />

    <div v-if="lineItem.referenceNumbers.length" class="row border-top">
      <ReferenceNumbersEdit
        v-model="lineItem.referenceNumbers"
        :disabled="disabled"
        :levels="[ReferenceNumberTypeLevel.LINE_ITEM]"
        class="w-8"
        hide-add-button
      />
    </div>
  </div>
  <LineItemEditGrid
    v-else
    v-model="lineItem"
    :index="index"
    :handling-units="handlingUnits"
    :error-response="errorResponse"
    :removable="removable"
    :show-stops="showStops"
    :show-save-product-switch="showSaveProductSwitch"
    :disable-default-reference-numbers="disableDefaultReferenceNumbers"
    class="line-item-edit-content w-full"
    @remove="emit('remove', $event)"
  />
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  matches,
  maxValue,
  minValue,
  required,
  requiredIf,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  DIMS_UNITS,
  DimsUom,
  ErrorResponse,
  HandlingUnit,
  Loadable,
  ReferenceNumberTypeLevel,
  WEIGHT_UNITS,
} from '@rygen/rygen-ui/model';

import { useReferenceNumberTypes } from '@/composables';
import { LineItem, ShipmentLineItem } from '@/model';

import LineItemEditGrid from '@/components/lineItem/LineItemEditGrid.vue';
import LineItemStopsRow from '@/components/lineItem/LineItemStopsRow.vue';
import ProductQuickSearch from '@/components/lineItem/ProductQuickSearch.vue';
import ReferenceNumbersEdit from '@/components/referenceNumber/ReferenceNumbersEdit.vue';
import DimensionsInputGroup from '@rygen/rygen-ui/components/form/DimensionsInputGroup.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import UnitOfMeasureInputGroup from '@rygen/rygen-ui/components/form/UnitOfMeasureInputGroup.vue';

const isExpanded = defineModel('expanded', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: LineItem,
    default: () => new LineItem(),
  },
  index: {
    type: Number,
    default: () => 1,
  },
  handlingUnits: {
    type: Object as PropType<Loadable<HandlingUnit[]>>,
    default: () => ({ loading: false }),
  },
  errorResponse: {
    type: Object as PropType<ErrorResponse>,
    default: () => undefined,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  showStops: {
    type: Boolean,
    default: () => false,
  },
  disableDefaultReferenceNumbers: {
    type: Boolean,
    default: () => false,
  },
  showSaveProductSwitch: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  index,
  handlingUnits,
  errorResponse,
  removable,
  disabled,
  showStops,
  disableDefaultReferenceNumbers,
  showSaveProductSwitch,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'clear'): void;
  (event: 'duplicate', lineItem: LineItem): void;
  (event: 'remove', lineItem: LineItem): void;
  (event: 'update:model-value', newVal: LineItem): void;
}>();

const Clazz = LineItem.getSubclass(modelValue.value);
const lineItem: Ref<LineItem> = ref(new Clazz(modelValue.value));

const { t } = useI18n();
const {
  getDefaultReferenceNumbers,
  lineItemReferenceNumberTypes,
  loadReferenceNumberTypes,
} = useReferenceNumberTypes();

const rules = computed(() => {
  const r: Record<string, any> = {
    description: {
      required: required(t('common.description')),
    },
    quantity: {
      required: required(t('lineItems.handling')),
      minValue: minValue(1, t('lineItems.handling')),
    },
    handlingUnit: {
      required: required(
        t('common.paramUom', { param: t('lineItems.handling') }),
      ),
    },
    packagingQty: {
      minValue: minValue(0, t('lineItems.packaging')),
    },
    packagingUnit: {
      required: requiredIf(
        () => lineItem.value.packagingQty,
        t('common.paramUom', { param: t('lineItems.packaging') }),
      ),
    },
    weight: {
      required: required(t('lineItems.weight')),
      minValue: minValue(1, t('lineItems.weight')),
    },
    weightUom: {
      required: required(
        t('common.paramUom', { param: t('lineItems.weight') }),
      ),
    },
    lengthDim: {
      minValue: minValue(1, t('dimensions.length')),
    },
    widthDim: {
      minValue: minValue(1, t('dimensions.width')),
    },
    heightDim: {
      minValue: minValue(1, t('dimensions.height')),
    },
    dimensionalUom: {
      required: required(t('lineItems.unitOfMeasure')),
    },
    freightClass: {},
    productNumber: {},
    linear: {
      matches: matches(/^[0-9]*$/, t('common.units.linearFeet')),
    },
    nmfcNumber: {
      matches: matches(/^\d{5,6}(-\d{1,2})?$/, t('lineItems.nmfcNumber')),
    },
    hazmatData: {
      unNumber: {
        matches: matches(/^(UN|NA)[0-9]{4}$/, t('lineItems.unOrNaNumber')),
      },
      hazardDescription: {},
      hazardClass: {},
      packingGroup: {},
    },
    temperatureSettings: {
      needsRefrigeration: {},
      minTemp: {
        maxValue: maxValue(
          lineItem.value.temperatureSettings.maxTemp,
          t('temperatureSettings.minTemp'),
        ),
      },
      maxTemp: {
        minValue: minValue(
          lineItem.value.temperatureSettings.minTemp,
          t('temperatureSettings.maxTemp'),
        ),
      },
      temperatureUom: {},
    },
  };

  if (lineItem.value.hazmatData.hazmat) {
    r.hazmatData.unNumber.required = required(t('lineItems.unOrNaNumber'));
    r.hazmatData.hazardDescription.required = required(
      t('lineItems.hazardDescription'),
    );
    r.hazmatData.hazardClass.required = required(t('lineItems.hazardClass'));
    r.hazmatData.packingGroup.required = required(t('lineItems.packingGroup'));
  }

  if (lineItem.value.temperatureSettings.needsRefrigeration) {
    r.temperatureSettings.minTemp.required = required(
      t('temperatureSettings.minTemp'),
    );
    r.temperatureSettings.maxTemp.required = required(
      t('temperatureSettings.maxTemp'),
    );
    r.temperatureSettings.temperatureUom.required = required(
      t('temperatureSettings.tempUnit'),
    );
  }

  return r;
});
const { v$ } = useValidations(lineItem, rules);

const id = computed(() => `line-item-num-${index.value + 1}`);

const availableDimsUoms = computed(() => {
  return DIMS_UNITS.filter(
    (uom) => uom.value !== DimsUom.METER && uom.value !== DimsUom.YARD,
  );
});

watch(
  () => lineItemReferenceNumberTypes.value.data,
  (newVal) => {
    if (!newVal?.length || disableDefaultReferenceNumbers.value) {
      return;
    }

    const defaultReferenceNumbers = getDefaultReferenceNumbers(newVal);
    if (
      defaultReferenceNumbers.length &&
      !lineItem.value.referenceNumbers.length
    ) {
      lineItem.value.referenceNumbers = defaultReferenceNumbers;
    }
  },
);

watch(
  modelValue,
  (newVal: LineItem) => {
    lineItem.value = new Clazz(newVal);
  },
  { immediate: true, deep: true },
);

watch(
  lineItem,
  (newVal: LineItem) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);

onMounted(() => {
  if (!disabled.value) {
    loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.LINE_ITEM,
    });
  }
});
</script>

<style>
.line-item-edit-content {
  display: flex;
  flex-direction: column;
  gap: var(--gap-1);

  .row {
    display: flex;
    gap: var(--sp-3);
    align-items: start;
    padding: 0 var(--sp-6);

    &.border-top {
      border-top: var(--divider);
      padding-top: var(--sp-3);
    }
  }
}
</style>
